<template>
    <b-container fluid v-if="staylioSettings">
        <b-row>
            <b-col lg="4" md="8" sm="12">
                <b-row>
                    <b-col :md="12">
                        <h5 class="mb-1">Staylio App Access</h5>
                        <p class="text-muted mb-0">
                            Disabling guest access to the Staylio app will remove all app references and links from
                            future communications. Existing magic links will become invalid, and new magic links will no
                            longer be generated.
                        </p>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col :md="12">
                        <br />
                        <table class="table">
                            <tbody>
                                <b-form-group description="">
                                    <form-switch
                                        v-model="staylioSettings.staylio_enabled"
                                        @change="updateSettings(staylioSettings)"
                                    />
                                    <label>Allow guest to access Staylio</label>
                                </b-form-group>
                            </tbody>
                        </table>
                    </b-col>
                </b-row>
            </b-col>
        </b-row>

        <b-row v-if="staylioSettings.staylio_enabled">
            <b-col lg="4" md="8" sm="12">
                <b-row>
                    <b-col :md="12">
                        <h5 class="mb-1">Staylio App Settings</h5>
                        <p class="text-muted mb-0">
                            Tailor guest access permissions across all properties and reservations in the Staylio app.
                        </p>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col :md="12">
                        <br />
                        <table class="table">
                            <tbody>
                                <b-form-group description="">
                                    <form-switch
                                        v-model="staylioSettings.allow_guest_reviews"
                                        @change="updateSettings(staylioSettings)"
                                    />
                                    <label>Allow guest to leave reviews</label>
                                </b-form-group>
                                <b-form-group v-if="digitalSignatureEnabled" description="">
                                    <form-switch v-model="enableGuestApp" />
                                    <label>Allow guest to sign and review rental agreements</label>
                                </b-form-group>
                                <b-form-group description="" v-if="!enableGuestApp && digitalSignatureEnabled">
                                    <form-switch
                                        v-model="staylioSettings.allow_rental_agreements"
                                        @change="updateSettings(staylioSettings)"
                                    />
                                    <label>Allow guest to view rental agreements</label>
                                </b-form-group>
                                <b-form-group :description="secureRentalInformationDescription">
                                    <label>Allow guest to view secure rental information: </label>
                                    <form-select
                                        v-model="staylioSettings.secure_rental_information_available_at"
                                        @change="updateSettings(staylioSettings)"
                                        :options="staylioSettings.secure_rental_information_available_at_choices"
                                    />
                                </b-form-group>
                            </tbody>
                        </table>
                    </b-col>
                </b-row>
            </b-col>
        </b-row>
    </b-container>
</template>

<script>
    import { mapGetters } from 'vuex';
    import API from '../../api';
    import { STAYLIO_SETTINGS_ENDPOINT } from '../../endpoints';
    import page from '../generic/page/page.vue';
    import { formatResponseError } from '../../utils/api';
    import FormSwitch from '../generic/form-switch/form-switch.vue';
    import FormSelect from '../generic/form-select/form-select.vue';

    export default {
        components: {
            page,
            FormSwitch,
            FormSelect,
        },
        name: 'StaylioAppSettings',
        data() {
            return {
                staylioSettings: null,
                busy: false,
            };
        },
        computed: {
            ...mapGetters('header', ['userCurrencySymbol']),
            enableGuestApp: {
                get() {
                    return this.$store.getters['digitalSignature/enableGuestApp'];
                },
                set(value) {
                    this.saveSetting('enableGuestApp', value);
                },
            },
            digitalSignatureEnabled: {
                get() {
                    return this.$store.getters['digitalSignature/enable'];
                },
            },
            secureRentalInformationDescription() {
                return `Secure rental information includes directions, Wi-Fi and door codes, check-in details, 
                parking instructions, and custom property variables. As this information may include sensitive 
                property details, this setting enables you to control when guests can access it via the Staylio App.`;
            },
        },
        mounted() {
            this.fetchAllData();
        },
        methods: {
            async updateSettings(staylioSettings) {
                try {
                    this.busy = true;
                    await API.patch(STAYLIO_SETTINGS_ENDPOINT(), staylioSettings);
                    this.successToast({ text: 'Settings successfully saved' });
                } catch (e) {
                    const { generic } = formatResponseError(e);
                    this.errorToast({ text: generic || 'Could not save settings' });
                } finally {
                    this.busy = false;
                }
            },
            async fetchAllData() {
                this.busy = true;
                await this.fetchSettings();
                try {
                    await this.$store.dispatch('digitalSignature/fetch');
                } catch (err) {
                    this.errorToast({ text: 'Could not fetch settings.' });
                    return;
                }
                this.busy = false;
            },
            async fetchSettings() {
                try {
                    this.busy = true;
                    const { data } = await API.get(STAYLIO_SETTINGS_ENDPOINT());
                    this.staylioSettings = data;
                } catch (e) {
                    const { generic } = formatResponseError(e);
                    this.errorToast({ text: generic || 'Could not fetch settings' });
                } finally {
                    this.busy = false;
                }
            },
            saveSetting(key, value) {
                (async () => {
                    try {
                        await this.$store.dispatch('digitalSignature/save', {
                            key,
                            value,
                        });
                        this.successToast({ text: 'Settings successfully saved' });
                    } catch (err) {
                        this.errorToast({ text: 'Could not save setting.' });
                    }
                })();
            },
        },
    };
</script>

<style lang="scss" scoped></style>
